import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { Guid } from "@/Common/Models/Guid";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { EncounterItemTypeEnum } from "@/Patient/Models/MedicalSummary/EncounterItemTypeEnum";
import { MedicalItemSubtypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemSubTypeEnum";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";

class Encounter {
    public Id: Guid;
    public Version: number;
    public CreationDate: Date;
    public CreatedBy: string;
    public LastUpdateDate: Date;
    public LastUpdatedBy: string;
    public DeletionDate: Date;
    public DeletedBy: string;
    public ArchivalDate: Date;
    public ArchivedBy: string;
    public IsSignedOff: boolean;
    public ConsultationDate: Date;
    public OriginalConsultationDate: Date;
    public IsInvalid: boolean;
    public SignedOffDate: Date;
    public PrecisionDate: number;
    public VisitPurpose: string;
    public HistoricalNote: string;
    public ExamenNote: string;
    public LabNote: string;
    public RadiologyNote: string;
    public OtherPlanNote: string;
    public ProgressNote: string;
    public ConsultationTypeLookupId: string;
    public ConsultationContextLookupId: string;
    public ConsultationId: Guid;
    public SignedOffByEmployeeId: Guid;
    public CreatedByEmployeeId: Guid;
    public ProblemId: string;
    public PatientId: string;
    public SiteId: Guid;
    public PrintedOnDate: Date;
    public PrintedByEmployeeId: Guid;
    public CostCenterId: Guid;
    public AutoApprove: boolean;
    public SearchIndex?: string;
    public AppointmentId?: string;
    public SuggestedAppointmentLinkId?: string;
    public EncounterRelatedEmployees: EncounterRelatedEmployee[];
    public EnableAppointmentLinkSuggestions: boolean;
    public IsImplicitlyApproved: boolean;

    constructor(init?: Partial<Encounter>) {
        Object.assign(this, init);
    }

    public static GetCurrentRelatedEmployee(encounter: Encounter): EncounterRelatedEmployee {
        if (encounter != null && encounter.EncounterRelatedEmployees != null) {
            const currentEmps = encounter.EncounterRelatedEmployees.filter(x => x.EmployeeId == sessionStore.getCurrentEmployee().ContactFacetId);
            if (currentEmps.length > 0)
                return currentEmps[0];
        }
        return null;
    }

    public static getCurrentUserEnabledSites(encounter: Encounter): Immutable.List<SitesModel> {
        const firstContributorEmployeeId = encounter.EncounterRelatedEmployees
            .sortByProperty(item => Date.parse(item.FirstContribution))
            .map(x => x.EmployeeId)
            .firstOrDefault();

        const firstContributorSiteIds = cacheStore.GetEmployeeByContactFacetId(firstContributorEmployeeId)
            .AssociatedSites;

        // we want to get all sites that the creator of the note currently has permissions on, as well as the site
        const allowedSiteIds = [encounter.SiteId].concat(firstContributorSiteIds).distinct();
        return allowedSiteIds.map(x => cacheStore.GetSiteById(x)).toImmutable();
    }

    public static getCurrentUserEnabledSitesAsComboBoxItems(encounter: Encounter): Immutable.List<ComboBoxDropDownItem> {
        return Encounter.getCurrentUserEnabledSites(encounter)
            .map(site => SitesModel.ToComboboxItem(site)).toList();
    }
}

class EncounterRelatedEmployee {
    public EncounterId: Guid;
    public EmployeeId: Guid;
    public FirstContribution: string; // Date
    public LastContribution: string; // Date
    public DurationTicks: string; // NOTE: This can be a number. Confirmed here MedicalNoteHeader component line 133.

    constructor(init?: Partial<EncounterRelatedEmployee>) {
        (Object as any).assign(this, init);
    }
}

class ItemBlockingDeleteAction {
    public ItemId: string;
    public Name: string;
    public ItemDate: Date;
    public Type: EncounterItemTypeEnum;
    public MedicalItemType?: MedicalItemTypeEnum;
    public MedicalItemSubType?: MedicalItemSubtypeEnum;
    public EntryCount: number;

    public static getEncounterItemTypeLabel(itemType: EncounterItemTypeEnum): string {
        switch (itemType) {
            case EncounterItemTypeEnum.ClinicalForms:
                return cacheStore.GetString("ClinicalFormLabel");

            case EncounterItemTypeEnum.PatientForms:
                return cacheStore.GetString("FormsLabel");

            case EncounterItemTypeEnum.APSSForm:
                return cacheStore.GetString("APSSLABEL");

            case EncounterItemTypeEnum.Prescription:
                return cacheStore.GetString("FormPrescription");

            case EncounterItemTypeEnum.PrivateBill:
                return cacheStore.GetString("PrivateBillingLabel");

            case EncounterItemTypeEnum.MedfarBilling:
                return cacheStore.GetString("BillingLabel");

            case EncounterItemTypeEnum.Request:
                return cacheStore.GetString("RequestsLabel");

            case EncounterItemTypeEnum.ConsultationRequest:
                return cacheStore.GetString("ConsultationRequestLabel");

            case EncounterItemTypeEnum.HandwrittenNotes:
                return cacheStore.GetString("HandwrittenNotesLabel");

            case EncounterItemTypeEnum.MedicalItem:
                return cacheStore.GetString("ProblemesActifsLabel");

            case EncounterItemTypeEnum.CnesstForm:
                return cacheStore.GetString("Cnesst");

            case EncounterItemTypeEnum.ObstetricalFile:
                return cacheStore.GetString("ObstetricalFileLabel");

            case EncounterItemTypeEnum.WorkSafeBc:
                return cacheStore.GetString("WorkSafeBc_WorkSafeBcTitleLabel");

            case EncounterItemTypeEnum.Flowsheet:
                return cacheStore.GetString("Flowsheets_FlowsheetsLabel");

            default:
                return "";
        }
    }
}

export { Encounter, EncounterRelatedEmployee, ItemBlockingDeleteAction };
